import { ArrowUUpLeft, Check, Eye, X } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { actions } from "astro:actions";
import clsx from "clsx";
import { motion, type Variants } from "motion/react";
import type React from "react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { useLocale } from "~/hooks/locale";
import { clockTime } from "~/utils/time";

import { DialogBody } from "../DialogBody";
import { DialogFooter } from "../DialogFooter";
import { DialogHeader } from "../DialogHeader";
import { FeedbackWidget } from "../FeedbackWidget";
import { Footer } from "../Footer";
import { getLongestStreak, getPoints } from "../Game";
import { useGame } from "../GameContext";
import { GameLabel } from "../GameLabel";
import { MarketingTitle } from "../MarketingTitle";
import { Modal } from "../Modal";
import { PackGrid } from "../PackGrid";
import { LastWord } from "./LastWord";
import { PostGameStat } from "./PostGameStat";

const variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
  },
} satisfies Variants;

export const PostGame: React.FC<{
  header: React.ReactElement;
}> = ({ header }) => {
  const { t } = useLocale();
  const { game, resetGame } = useGame();

  const [isReady, setIsReady] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const [animate, setAnimate] = useState<keyof typeof variants>();
  const lastWord = useMemo(
    () => game.words[game.answers.length] ?? game.words[game.words.length - 1],
    [game],
  );

  const correctAnswers = useMemo(
    () => game.answers.filter((answer) => answer.correct).length,
    [game.answers],
  );

  const heading = useMemo(() => {
    const accuracy =
      game.answers.length && correctAnswers / game.answers.length;
    return (
      <motion.h1
        layout="position"
        layoutId="heading"
        className="m-auto text-center text-7xl heading md:text-8xl"
        onLayoutAnimationComplete={() => {
          setAnimate("visible");
        }}
      >
        {!accuracy
          ? t("nice_try")
          : accuracy === 1
            ? t("perfect")
            : accuracy > 0.5
              ? t("great_job")
              : t("good_game")}
      </motion.h1>
    );
  }, [game.answers.length, correctAnswers, t]);

  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    /**
     * Layout animations are cancelled on resize, so the onLayoutAnimationComplete
     * callback is never called. This function manually triggers the fadeIn animation
     * on resize.
     */
    const handleResize = () => {
      setAnimate("visible");
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timer.current); // cleanup timeout on unmount
    };
  }, []);

  const id = useId();
  const { data } = useQuery({
    staleTime: Infinity,
    queryKey: ["packs", id],
    queryFn: () =>
      actions.game.getSuggestedPacks.orThrow({
        category: undefined,
      }),
  });

  if (!isReady || !data) {
    return (
      <div
        className="flex min-h-screen"
        ref={() => {
          timer.current = setTimeout(() => {
            setIsReady(true);
          }, 500);
        }}
      >
        {heading}
      </div>
    );
  }

  return (
    <>
      <motion.div
        variants={variants}
        initial="hidden"
        animate={animate}
        className="inline"
      >
        {header}
      </motion.div>
      <div className="flex items-center pad pad-md">{heading}</div>
      <motion.div
        className="border-t"
        variants={variants}
        initial="hidden"
        animate={animate}
      >
        <div className="relative wrap pad pad-md">
          <GameLabel direction="down">{t("game_summary")}</GameLabel>
          <div className="space-y-12 md:space-y-16">
            <div className="grid gap-10 sm:grid-cols-2 lg:gap-12">
              <div className="space-y-6 md:space-y-8">
                <div className="space-y-4">
                  <PostGameStat value={clockTime(game.time / 1000)}>
                    {t("total_time")}
                  </PostGameStat>
                  <PostGameStat value={game.answers.length.toLocaleString()}>
                    {game.answers.length > 0 ? (
                      <button
                        onClick={() => {
                          setIsModalOpen(true);
                        }}
                        className="link flex items-center gap-1"
                      >
                        {t("words_viewed")}
                        <Eye className="size-4" weight="bold" />
                      </button>
                    ) : (
                      t("words_viewed")
                    )}
                  </PostGameStat>
                  <PostGameStat value={correctAnswers.toLocaleString()}>
                    {t("correct_answers")}
                  </PostGameStat>
                  <PostGameStat
                    value={getPoints(game.answers).toLocaleString()}
                  >
                    {t("points")}
                  </PostGameStat>
                  <PostGameStat
                    value={getLongestStreak(game.answers).toLocaleString()}
                  >
                    {t("longest_streak")}
                  </PostGameStat>
                </div>
                <button
                  className="btn btn-secondary btn-block"
                  onClick={resetGame}
                >
                  {t("play_again")}
                  <ArrowUUpLeft weight="bold" className="size-5" />
                </button>
              </div>
              <div>{lastWord && <LastWord word={lastWord} />}</div>
            </div>
            <div className="space-y-4 md:space-y-6">
              <MarketingTitle
                size="sm"
                subtitle={t("ready_for_more")}
                title={t("suggested_packs")}
              />
              <PackGrid packs={data} />
            </div>
          </div>
        </div>
        <Footer />
      </motion.div>
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <DialogHeader>{t("words_viewed")}</DialogHeader>
        <DialogBody>
          <ul className="grid grid-cols-2 gap-3">
            {game.answers.map((answer, index) => {
              const Icon = answer.correct ? Check : X;
              return (
                <li key={index} className="flex items-center gap-2">
                  <Icon
                    className={clsx(
                      "size-5",
                      answer.correct ? "text-success" : "text-error",
                    )}
                    weight="bold"
                  />
                  <span>{answer.solution}</span>
                </li>
              );
            })}
          </ul>
        </DialogBody>
        <DialogFooter>
          <div className="grid grow grid-cols-2 gap-3">
            <button onClick={closeModal} className="btn">
              {t("cool")}
              <span>👍</span>
            </button>
            <button onClick={closeModal} className="btn btn-secondary">
              {t("super_cool")}
              <span>👍👍</span>
            </button>
          </div>
        </DialogFooter>
      </Modal>
      <FeedbackWidget />
    </>
  );
};
